/**
 * Set contents ("구성품") written as text in the legacy detail sections.
 *
 * hinata camping sets keep one section per season, in all 5 languages:
 *   "봄 구성품: 어메니티 돔 S ×1 · 폼 매트 180 ×2 · Explorer EX-V777D 랜턴 ×1"
 *   "春 構成品: アメニティドームS ×1 · …" / "Spring Included: …" / "春季 包含装备: …"
 * The product page used to drop these whenever the detail HTML existed (the
 * HTML only shows the list as an English picture), so the most product-specific
 * text never reached the page. Pure module — no React, no fetching.
 */

export interface ComponentSection {
  /** Prefix as written ("봄 구성품", "Spring Included"). */
  label: string;
  items: string[];
}

interface SectionLike {
  description?: string | null;
  display_order?: number | null;
}

/** "{season} {word}: a · b · c" — the word is what every hinata row uses per language. */
const COMPONENT_LINE =
  /^\s*([^:：\n]{1,24}?(?:구성품|構成品|Included|包含装备|包含裝備))\s*[:：]\s*([\s\S]+?)\s*$/;

const ITEM_SEPARATOR = /\s+·\s+/;
const COUNT_SUFFIX = /\s*×\s*\d+\s*$/;

/**
 * Component names whose quantity the partner has not confirmed
 * (`specifications.hinata.issues[].component`). Internal notes themselves are
 * never shown; the item stays, only its "×N" is dropped.
 */
export function disputedComponentNames(
  specifications: Record<string, unknown> | null | undefined,
): string[] {
  const hinata = specifications?.hinata;
  if (!hinata || typeof hinata !== "object") return [];
  const issues = (hinata as { issues?: unknown }).issues;
  if (!Array.isArray(issues)) return [];
  const names: string[] = [];
  for (const issue of issues) {
    const name = (issue as { component?: unknown })?.component;
    if (typeof name === "string" && name.trim()) names.push(name.trim());
  }
  return names;
}

/** One section → label + items, or null when it is not a component list. */
export function parseComponentLine(
  text: string | null | undefined,
  disputed: readonly string[] = [],
): ComponentSection | null {
  if (!text) return null;
  const match = COMPONENT_LINE.exec(text);
  if (!match) return null;
  const items = match[2]
    .split(ITEM_SEPARATOR)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) =>
      disputed.some((name) => item.includes(name)) ? item.replace(COUNT_SUFFIX, "") : item,
    );
  if (items.length === 0) return null;
  return { label: match[1].trim(), items };
}

/** All component sections in display order. Other (marketing) sections are skipped. */
export function parseComponentSections(
  sections: readonly SectionLike[] | null | undefined,
  disputed: readonly string[] = [],
): ComponentSection[] {
  return [...(sections ?? [])]
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    .map((section) => parseComponentLine(section.description, disputed))
    .filter((section): section is ComponentSection => section !== null);
}

/** Items present in every section (same name and count), in the first section's order. */
export function commonComponentItems(sections: readonly ComponentSection[]): string[] {
  if (sections.length === 0) return [];
  const [first, ...rest] = sections;
  return first.items.filter((item) => rest.every((s) => s.items.includes(item)));
}
